import { HttpErrorResponse, HttpHeaders, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { AlcmeonContextService } from '../alcmeon-context/alcmeon-context.service';
import { CordovaService } from '../cordova.service';
import { WebSocketEventsHandler } from '../websocket/reconnecting-websocket';
import { TrackClient } from './track-client';
import { TrackLoggerService } from './track-logger.service';

const RESPONSE_HEADERS_WHITELIST = [
  'content-type',
  'content-encoding',
  'content-length',
  'x-alcmeon-request-id',
  'x-alcmeon-state',
  'last-modified',
  'etag',
  'cache-control',
  'content-language',
  'expires',
  'pragma',
];

@Injectable({
  providedIn: 'root',
})
export class TrackService implements WebSocketEventsHandler {
  private trackClient: TrackClient;

  constructor(
    private cordovaService: CordovaService,
    private alcmeonContext: AlcmeonContextService,
    trackLoggerService: TrackLoggerService
  ) {
    this.trackClient = new TrackClient(this.cordovaService, this.alcmeonContext);
    this.pushPending('client-session-init', {
      width: window.innerWidth,
      height: window.innerHeight,
    });
    trackLoggerService.setTrackService(this);
  }

  get sessionId() {
    return this.trackClient.sessionId;
  }

  // also called by xhr-stats.service.js
  pushPending(type, event) {
    this.trackClient.pushPending(type, event);
  }

  // also called by xhr-stats.service.js
  pushOrAggregatePending(type, subevent) {
    this.trackClient.pushOrAggregatePending(type, subevent);
  }

  // also called by xhr-stats.service.js
  pushOrOverwritePending(type, subevent) {
    this.trackClient.pushOrOverwritePending(type, subevent);
  }

  onHttpRequest(request: HttpRequest<any>, requestTimestamp: number) {
    let body = request.body;
    if (request.body && request.body.password) {
      body = {
        email: request.body.email,
        password: '*********',
      };
    }
    const event = {
      headers: this.serializeHttpHeaders(request.headers, true),
      method: request.method,
      params: request.params ? request.params.keys() : [],
      url: request.url,
      data: this.toStringMaxLength(body),
      request_timestamp: requestTimestamp,
    };
    this.pushPending('http-request', event);
  }

  onHttpResponse(response: HttpResponse<any> | HttpErrorResponse, request: HttpRequest<any>, requestTimestamp: number) {
    const data = response instanceof HttpResponse ? response.body : response.error;
    const event = {
      headers: this.serializeHttpHeaders(response.headers, false),
      status: response.status,
      statusText: response.statusText,
      data: this.toStringMaxLength(data),
      // track expects a few fields from the event, otherwise the indexer will crash
      response_timestamp: Math.round(performance.now()),
      config: {
        request_timestamp: requestTimestamp,
        method: request.method,
        url: response.url,
      },
    };
    this.pushPending('http-response', event);
  }

  onHttpErrorResponse(responseError: HttpErrorResponse, request: HttpRequest<any>, requestTimestamp: number) {
    // for now let's ignore cases caused by a js error (see https://angular.io/guide/http#getting-error-details )
    this.onHttpResponse(responseError, request, requestTimestamp);
  }

  onWsOpen() {
    this.pushPending('ws-open', {});
  }

  onWsClose() {
    this.pushPending('ws-close', {});
  }

  onWsError() {
    this.pushPending('ws-error', {});
  }

  onWsSuccess() {
    this.pushPending('ws-success', {});
  }

  onWsEvent(message: string) {
    // message has been compacted already, but let's use a timestamp in seconds instead of milliseconds
    // (this alone should save over a GB of storage per week of data !)
    this.pushOrAggregatePending('ws-event', [Math.round(Date.now() / 1000), message]);
  }

  private serializeHttpHeaders(headers: HttpHeaders, isRequest: boolean): object {
    const result = {};
    for (const name of headers.keys()) {
      if (isRequest || RESPONSE_HEADERS_WHITELIST.includes(name.toLowerCase())) {
        result[name] = headers.get(name);
      }
    }
    return result;
  }

  private toStringMaxLength(value) {
    let result;
    try {
      result = JSON.stringify(value);
    } catch (error) {
      result = 'probably a cyclic object';
    }
    if (result && result.length > 100) {
      // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
      result = result.substring(0, 100) + '\u2026';
    }
    return result;
  }
}
